import { DateTime } from 'luxon';

/**
 * Formats phone numbers according to DIN 5008 standards
 */
export const normalizePhoneDIN5008 = (raw: string): string => {
	if (!raw) return '';
	const cleaned = raw.trim();

	// The logic for German spacing: Area Code (3-5 digits) | Subscriber (3-5 digits) | Remaining
	const germanSpacingRegex = /^(\d{3,5})(\d{3,5})(\d+)?$/;
	const formatMatch = (_: string, a: string, b: string, c: string) => [a, b, c].filter(Boolean).join(' ');

	// 1. International format
	if (cleaned.startsWith('+')) {
		const numeric = cleaned.replace(/[^\d+]/g, '');
		if (numeric.startsWith('+49')) {
			const local = numeric.slice(3);
			return `+49 ${local.replace(germanSpacingRegex, formatMatch)}`;
		}
		return numeric;
	}

	// 2. Local German format (e.g., 030...)
	if (/^0\d+/.test(cleaned)) {
		const digits = cleaned.replace(/[^\d]/g, '');
		return digits.replace(germanSpacingRegex, formatMatch);
	}

	return cleaned;
};

export const getInitials = (name: string): string =>
	name
		? name
				.split(/\s+/)
				.map((word) => word[0])
				.join('')
				.toUpperCase()
		: '';

export const formatAddressLine = ({
	street,
	houseNumber,
	postalCode,
	city
}: {
	street?: string;
	houseNumber?: string;
	postalCode?: string;
	city?: string;
}): string[] => {
	const line1 = [street, houseNumber].filter(Boolean).join(' ');
	const formattedZip = postalCode ? `D-${postalCode}` : '';
	const line2 = [formattedZip, city].filter(Boolean).join(' ');

	return [line1, line2].filter(Boolean);
};

/**
 * Normalizes a date string into a specific format.
 * @param rawDate - The input date string, number, or null.
 * @param format - Optional. The Luxon format string (e.g., 'dd.MM.yyyy' or 'dd/MM/yyyy HH:mm').
 * Defaults to 'dd.MM.yyyy'.
 */
export const normalizeDateLabel = (rawDate?: string | number | null): string => {
	if (!rawDate) return '';

	let dt: DateTime;

	// Check for number type OR a string that contains only digits
	const isNumeric = typeof rawDate === 'number' || /^\d+$/.test(String(rawDate));

	if (isNumeric) {
		dt = DateTime.fromMillis(Number(rawDate));
	} else {
		const dateStr = String(rawDate).trim();

		// 1. Try ISO (2026-11-10T00:00:00Z)
		dt = DateTime.fromISO(dateStr);

		// 2. Fallback to SQL format (2026-11-10 00:00:00)
		if (!dt.isValid) {
			dt = DateTime.fromSQL(dateStr);
		}

		// 3. Fallback to: 09/05/2026 10:45
		if (!dt.isValid) {
			dt = DateTime.fromFormat(dateStr, 'dd/MM/yyyy HH:mm');
		}

		// 4. Fallback to: 09.05.2026
		if (!dt.isValid) {
			dt = DateTime.fromFormat(dateStr, 'dd.MM.yyyy');
		}

		// 5. Final JS Date fallback
		if (!dt.isValid) {
			const jsDate = new Date(dateStr);
			if (!Number.isNaN(jsDate.getTime())) {
				dt = DateTime.fromJSDate(jsDate);
			}
		}
	}

	if (dt?.isValid) {
		return dt.setZone('Europe/Berlin').setLocale('de-DE').toFormat('dd.MM.yyyy');
	}

	return String(rawDate);
};

export const formatDisplayUrl = (url: string) => {
	if (!url) return '';
	// Remove protocol (http/https) and www
	let display = url.replace(/^(https?:\/\/)?(www\.)?/i, '');
	// Remove everything after the first slash (slugs/paths)
	display = display.split('/')[0];
	return display;
};
